import { NavLink, useLocation } from "react-router-dom";
import Layout from "../../components/Layout/Layout";

export const inputClass =
  "w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-red-500 focus:ring-2 focus:ring-red-100";

export const PAGE_SIZE = 20;

function sectionActive(pathname, target) {
  if (target === "leads") return pathname.includes("/leads");
  return pathname.startsWith("/products/jagran-next") && !pathname.includes("/leads");
}

export function PageShell({ title, description, actions, children }) {
  const { pathname } = useLocation();
  const links = [
    { id: "campaigns", to: "/products/jagran-next", label: "Campaigns" },
    { id: "leads", to: "/products/jagran-next/leads", label: "Leads" },
  ];

  return (
    <Layout>
      <main className="mx-auto max-w-6xl px-4 py-6 lg:px-0">
        <div className="overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-black/5">
          <div className="border-b border-gray-200 bg-white px-6 pt-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-red-600">Jagran Next</p>
                <h1 className="mt-1 text-2xl font-bold text-gray-900">{title}</h1>
                {description ? <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">{description}</p> : null}
              </div>
              {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
            </div>
            <nav className="mt-5 flex gap-6">
              {links.map((link) => {
                const active = sectionActive(pathname, link.id);
                return (
                  <NavLink
                    key={link.id}
                    to={link.to}
                    className={`border-b-2 pb-3 text-sm font-semibold ${
                      active ? "border-red-600 text-red-600" : "border-transparent text-gray-500 hover:text-gray-800"
                    }`}
                  >
                    {link.label}
                  </NavLink>
                );
              })}
            </nav>
          </div>
          <div className="bg-slate-50/60 p-6">{children}</div>
        </div>
      </main>
    </Layout>
  );
}

export function Panel({ title, description, children, className = "" }) {
  return (
    <section className={`rounded-xl border border-gray-200 bg-white p-5 shadow-sm ${className}`}>
      {title ? <h2 className="text-base font-semibold text-gray-900">{title}</h2> : null}
      {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
      <div className={title || description ? "mt-4" : ""}>{children}</div>
    </section>
  );
}

export function Field({ label, hint, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-1.5 flex items-center gap-1 text-sm font-semibold text-gray-800">
        {label}
        {required ? <span className="text-red-500">*</span> : null}
      </span>
      {children}
      {hint ? <span className="mt-1.5 block text-xs leading-5 text-gray-500">{hint}</span> : null}
    </label>
  );
}

export function EmptyState({ title, description }) {
  return (
    <div className="rounded-2xl border border-dashed border-gray-200 bg-slate-50 px-6 py-10 text-center">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      {description ? <p className="mt-1 text-sm text-gray-500">{description}</p> : null}
    </div>
  );
}

export function PrimaryButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function SecondaryButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function DangerButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`inline-flex items-center justify-center gap-2 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function TextButton({ children, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`text-sm font-semibold text-gray-600 hover:text-red-600 disabled:opacity-40 ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

export function StatusBadge({ value }) {
  const status = String(value || "unknown").toLowerCase();
  const tone =
    status === "active" || status === "confirmed"
      ? "bg-green-100 text-green-800"
      : status === "upcoming" || status === "lead" || status === "pending_payment"
        ? "bg-amber-100 text-amber-800"
        : status === "closed" || status === "ended" || status === "payment_failed"
          ? "bg-red-100 text-red-800"
          : "bg-gray-100 text-gray-700";
  return <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold capitalize ${tone}`}>{status.replace(/_/g, " ")}</span>;
}

export function Notice({ children, tone = "error" }) {
  if (!children) return null;
  const className =
    tone === "error"
      ? "mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 ring-1 ring-red-100"
      : "mb-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-gray-600 ring-1 ring-gray-100";
  return <div className={className}>{children}</div>;
}

export function Pager({ offset, limit, count, total, onPage }) {
  const page = Math.floor(offset / limit) + 1;
  const hasPrev = offset > 0;
  const hasNext = total != null ? offset + count < total : count >= limit;
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
      <span>
        Page {page}
        {total != null ? ` · ${total} records` : ""}
      </span>
      <div className="flex gap-2">
        <SecondaryButton disabled={!hasPrev} onClick={() => onPage(Math.max(offset - limit, 0))}>
          Previous
        </SecondaryButton>
        <SecondaryButton disabled={!hasNext} onClick={() => onPage(offset + limit)}>
          Next
        </SecondaryButton>
      </div>
    </div>
  );
}

export function formatWhen(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
}

export function toDateTimeInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (part) => String(part).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function toIso(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

export function registrationSummary(registration) {
  const data = registration?.data;
  if (!data || typeof data !== "object") return "-";
  return Object.entries(data)
    .map(([key, value]) => `${key}: ${value}`)
    .join(" · ");
}
